import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useIntercom } from 'react-use-intercom'

/**
 * useIntercomPageTracking - Logs a page view to Intercom on every navigation.
 *
 * Calls `react-use-intercom`'s `update()` with NO arguments on every
 * navigation after mount — including hash-only changes (e.g. `#subscription`,
 * `#taxes` sub-section anchors in the Settings page). Per the library's
 * typings, calling `update` without `props` is the documented way to initiate
 * a "ping" with an auto-generated `last_request_at`, which is exactly what
 * counts as a page view in Intercom. Passing `{}` would take the "update user
 * attributes" branch instead and miss the ping path.
 *
 * The effect listens to both `pathname` AND `hash` from `useLocation()` rather
 * than `pathname` alone. This captures hash-anchor navigations within the same
 * route (e.g. clicking "Taxes" under `/settings/organisation` produces
 * `/settings/organisation#taxes` — same pathname, new hash). `location.key`
 * was considered but rejected: it changes on every history push including
 * navigations to the exact same URL, which would over-count duplicate clicks.
 *
 * Mount this hook once at a global, in-router scope (e.g. SplitProviderWrapper)
 * so it observes every navigation across both public and authenticated routes.
 *
 * The first render is intentionally skipped — Intercom records the initial
 * page view as part of boot, so firing update() on mount would double-count.
 *
 * Pre-boot calls (e.g. navigating between login/register before Intercom has
 * booted) log a warning from react-use-intercom and are otherwise a no-op;
 * react-use-intercom does NOT queue these calls.
 *
 * @see QM-3097 - Improvements to TempusTools↔Intercom integration
 */
export const useIntercomPageTracking = () => {
    const { hash, pathname } = useLocation()
    const { update } = useIntercom()
    const isFirstRender = useRef(true)

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false
            return
        }
        update()
    }, [pathname, hash, update])
}
